import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Check, ArrowRight, BookOpen } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KAJABI, STUDIO_BG } from "@/lib/data";

const contents = [
 "How sync licensing actually works",
 "Setting up your business for real opportunity",
 "Rights, splits & PRO basics (so you don't lose money)",
 "Why most indie pitches fail (and how to fix yours)",
 "Where to pitch. And where not to waste your time",
 "The mindset of supervisors & what gets placed",
];

export default function Ebook() {
 const [form, setForm] = useState({ name: "", email: "" });
 const [submitted, setSubmitted] = useState(false);

 const onSubmit = (e) => {
 e.preventDefault();
 if (!form.email) {
 toast.error("Please enter your email.");
 return;
 }
 setSubmitted(true);
 toast.success("Redirecting you to your guide...");
 setTimeout(() => {
 window.open(KAJABI.ebook, "_blank", "noopener,noreferrer");
 }, 600);
 };

 return (
 <div data-testid="ebook-page">
 <section
 data-testid="ebook-hero"
 className="relative overflow-hidden grain border-b border-ink-600"
 >
 <div
 className="absolute inset-0 bg-cover bg-center opacity-40"
 style={{ backgroundImage: `url(${STUDIO_BG})` }}
 />
 <div className="absolute inset-0 bg-gradient-to-b from-ink-900/70 via-ink-900/90 to-ink-900" />

 <div className="relative max-w-7xl mx-auto px-4 md:px-8 pt-32 md:pt-40 pb-20 grid md:grid-cols-12 gap-12 items-center">
 {/* Book cover */}
 <motion.div
 initial={{ opacity: 0, x: -30 }}
 animate={{ opacity: 1, x: 0 }}
 transition={{ duration: 0.7 }}
 className="md:col-span-5 flex justify-center"
 >
 <div className="relative w-full max-w-sm aspect-[3/4] bg-gradient-to-br from-ink-800 to-ink-900 border border-ink-600 shadow-2xl group overflow-hidden">
 <div className="absolute inset-0 bg-gradient-to-br from-amber/20 via-transparent to-amber/5" />
 <div className="absolute top-8 left-8 right-8">
 <p className="tag-kicker">Free · Underground Mix</p>
 </div>
 <div className="absolute bottom-8 left-8 right-8">
 <BookOpen className="text-amber mb-6" size={44} />
 <p className="font-display text-4xl md:text-5xl font-bold leading-[0.95] tracking-tight">
 Survival <br />
 Guide for <br />
 <span className="text-amber italic">Sync.</span>
 </p>
 <div className="mt-6 h-[2px] w-16 bg-amber" />
 <p className="mt-4 font-mono text-xs uppercase tracking-widest text-white/60">
 For songwriters · artists · producers
 </p>
 </div>
 <div className="absolute left-0 top-0 bottom-0 w-1 bg-amber" />
 </div>
 </motion.div>

 {/* Form */}
 <motion.div
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ duration: 0.7, delay: 0.15 }}
 className="md:col-span-7"
 >
 <p className="tag-kicker">Free lead magnet</p>
 <h1 className="font-display text-4xl md:text-6xl font-bold leading-[0.98] tracking-tight mt-4">
 Get paid to have your music marketed - {" "}
 <span className="italic text-amber">
 not the other way around.
 </span>
 </h1>
 <p className="mt-6 text-lg text-white/70 max-w-xl">
 The Survival Guide for Sync is your initial set-up and music
 business primer. So your path into sync licensing starts right.
 </p>

 <ul className="mt-8 space-y-3">
 {contents.map((c, i) => (
 <li
 key={i}
 data-testid={`ebook-bullet-${i}`}
 className="flex items-start gap-3 text-white/85"
 >
 <Check size={18} className="text-amber mt-1 flex-shrink-0" strokeWidth={2.5} />
 <span>{c}</span>
 </li>
 ))}
 </ul>

 <form
 onSubmit={onSubmit}
 data-testid="ebook-form"
 className="mt-10 p-6 md:p-8 border border-ink-600 bg-ink-800 max-w-xl"
 >
 <div className="grid sm:grid-cols-2 gap-4">
 <div>
 <Label htmlFor="eb-name" className="text-xs uppercase tracking-widest font-mono text-white/60">
 First name
 </Label>
 <Input
 id="eb-name"
 data-testid="ebook-input-name"
 value={form.name}
 onChange={(e) => setForm({ ...form, name: e.target.value })}
 className="mt-2 bg-ink-900 border-ink-600 text-white"
 placeholder="Your name"
 />
 </div>
 <div>
 <Label htmlFor="eb-email" className="text-xs uppercase tracking-widest font-mono text-white/60">
 Email *
 </Label>
 <Input
 id="eb-email"
 type="email"
 data-testid="ebook-input-email"
 value={form.email}
 onChange={(e) => setForm({ ...form, email: e.target.value })}
 className="mt-2 bg-ink-900 border-ink-600 text-white"
 placeholder="you@yourmusic.com"
 />
 </div>
 </div>

 <button
 type="submit"
 data-testid="ebook-submit"
 disabled={submitted}
 className="mt-6 w-full inline-flex items-center justify-center gap-3 bg-amber hover:bg-amber-hover text-black font-bold uppercase tracking-widest text-sm px-7 py-4 rounded-sm transition-all group disabled:opacity-60"
 >
 {submitted ? "Opening your guide..." : "Send me the guide"}
 <ArrowRight
 size={16}
 className="transition-transform group-hover:translate-x-1"
 />
 </button>
 <p className="mt-3 text-xs text-white/45 font-mono">
 We never sell your info. Unsubscribe anytime.
 </p>
 </form>
 </motion.div>
 </div>
 </section>
 </div>
 );
}
